import { motion } from "framer-motion";
import { FileText, Search, Globe, Linkedin, CheckCircle, ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: FileText,
    title: "Content Creation",
    color: "#4F8EF7",
    tagline: "AI-Powered Copy at Scale",
    description: "From long-form blog articles to high-converting landing pages, our AI content engine produces brand-consistent, SEO-optimised content in a fraction of the time. We engineer narratives that engage, educate, and convert.",
    features: [
      "SEO-optimised blog posts and articles",
      "Landing page and ad copywriting",
      "Email sequences and newsletters",
      "Social media content calendars",
      "Brand voice development and guidelines",
    ],
  },
  {
    icon: Globe,
    title: "GEO / AEO Consultancy",
    color: "#8B5CF6",
    tagline: "Be Found in AI Search",
    description: "Generative Engine Optimisation (GEO) and Answer Engine Optimisation (AEO) are the new frontier. We optimise your content so it surfaces in ChatGPT, Gemini, Perplexity, and other AI-powered search surfaces where your audience is moving.",
    features: [
      "AI search engine visibility audit",
      "Structured data and schema markup",
      "Answer-intent content architecture",
      "Featured snippet and PAA targeting",
      "LLM visibility monitoring and reporting",
    ],
  },
  {
    icon: Search,
    title: "SEO Consultancy",
    color: "#4F8EF7",
    tagline: "Rank Smarter, Not Harder",
    description: "We leverage AI-driven keyword intelligence, technical audits, and predictive ranking models to build search strategies that compound over time. Your competition is still guessing; we already know what works.",
    features: [
      "Comprehensive technical SEO audits",
      "AI keyword research and gap analysis",
      "On-page and off-page optimisation",
      "Link building strategy and outreach",
      "Monthly ranking reports and insights",
    ],
  },
  {
    icon: Linkedin,
    title: "LinkedIn Marketing",
    color: "#8B5CF6",
    tagline: "Turn Connections into Clients",
    description: "LinkedIn is the highest ROI channel for B2B when done right. Our AI-enhanced LinkedIn strategy combines profile optimisation, thought leadership content, and precision outreach to fill your pipeline with qualified decision-makers.",
    features: [
      "Profile and company page optimisation",
      "Thought leadership content creation",
      "Targeted connection and outreach campaigns",
      "LinkedIn Ads strategy and management",
      "Analytics, A/B testing and performance reporting",
    ],
  },
];

const Services = () => {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="services-page"
      style={{ backgroundColor: "#0a0a0f", paddingTop: "5rem" }}
    >
      {/* Hero */}
      <section className="section-padding" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs font-mono uppercase tracking-widest mb-3"
            style={{ color: "#4F8EF7" }}
          >
            Our Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-5"
            data-testid="services-heading"
          >
            Built for <span className="gradient-text">Modern Growth</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base md:text-lg max-w-2xl mx-auto"
            style={{ color: "#a1a1aa" }}
          >
            Four expert-led, AI-enhanced services that work individually or together as a complete growth engine for your brand.
          </motion.p>
        </div>
      </section>

      {/* Service Cards */}
      <section className="pb-24" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <div className="flex flex-col gap-8">
            {services.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <motion.div
                  key={svc.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: 0.05 }}
                  data-testid={`services-card-${i}`}
                  className="group rounded-2xl overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.025)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    position: "relative",
                  }}
                  whileHover={{ borderColor: `${svc.color}35` }}
                >
                  {/* Top gradient line */}
                  <div style={{ height: "2px", background: `linear-gradient(90deg, ${svc.color}, ${svc.color === "#4F8EF7" ? "#8B5CF6" : "#4F8EF7"})` }} />

                  <div className="p-8 md:p-10">
                    <div className="flex flex-col md:flex-row gap-8 md:gap-12 md:items-center">
                      {/* Left: info */}
                      <div className="md:w-2/5 flex-shrink-0">
                        <div
                          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                          style={{ background: `${svc.color}15`, border: `1px solid ${svc.color}25` }}
                        >
                          <Icon size={26} style={{ color: svc.color }} strokeWidth={1.5} />
                        </div>
                        <p className="font-mono text-xs uppercase tracking-widest mb-2" style={{ color: svc.color }}>{svc.tagline}</p>
                        <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4 leading-tight">{svc.title}</h2>
                        <p className="text-sm leading-relaxed" style={{ color: "#a1a1aa" }}>{svc.description}</p>
                        <Link
                          to="/contact"
                          data-testid={`services-card-cta-${i}`}
                          className="inline-flex items-center gap-2 mt-6 text-sm font-medium transition-all duration-200"
                          style={{ color: svc.color }}
                          onMouseEnter={(e) => { e.currentTarget.style.gap = "10px"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.gap = "8px"; }}
                        >
                          Get in touch <ArrowRight size={15} />
                        </Link>
                      </div>

                      {/* Divider */}
                      <div className="hidden md:block w-px self-stretch" style={{ background: "rgba(255,255,255,0.06)" }} />

                      {/* Right: features — vertically centered */}
                      <div className="md:flex-1 flex flex-col justify-center">
                        <p className="font-semibold text-xs mb-5 uppercase" style={{ letterSpacing: "0.1em", color: "#52525b" }}>What's Included</p>
                        <ul className="space-y-4">
                          {svc.features.map((feat) => (
                            <li key={feat} className="flex items-start gap-3">
                              <CheckCircle size={16} style={{ color: svc.color, flexShrink: 0, marginTop: "2px" }} />
                              <span className="text-sm leading-relaxed" style={{ color: "#a1a1aa" }}>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding" style={{ backgroundColor: "#080810" }}>
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Not sure which service fits? <span className="gradient-text">Let's talk.</span>
            </h2>
            <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: "#a1a1aa" }}>
              Book a free 30-minute strategy call and we'll map the right services to your goals.
            </p>
            <Link
              to="/contact"
              data-testid="services-bottom-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-sm transition-all duration-300"
              style={{ background: "linear-gradient(135deg, #4F8EF7, #8B5CF6)", boxShadow: "0 0 25px rgba(79,142,247,0.4)" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 0 40px rgba(79,142,247,0.6)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 0 25px rgba(79,142,247,0.4)"; }}
            >
              Book a Free Strategy Call <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.main>
  );
};

export default Services;
